import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ingredientsBlockingRestriction, keywordsForRestriction, RESTRICTION_GROUPS } from '../../../../shared/allergenReference.js';
import type { MenuItem, Menu } from './menuCatalogTypes';
import type { DietBadge } from './formulaDietBadges';

/**
 * Les cartes du catalogue — plat et formule.
 *
 * Sorties de MenuCatalogPage.tsx qui dépassait la limite de 500 lignes. Ce sont des composants
 * PUREMENT d'affichage : tout ce dont ils ont besoin arrive en propriété, ils ne lisent aucun état et
 * n'appellent aucune API. C'est ce qui rend l'extraction sûre — aucune fermeture sur l'état de la page
 * n'a pu être perdue au passage.
 */

/** Carte d'un plat. Entièrement cliquable : viser le crayon était le seul moyen d'ouvrir un plat alors que
 *  toute la carte le désigne (demande d'Emeni, 2026-08-25, aligné sur les fiches Convives et Fournisseurs).
 *  La corbeille arrête la propagation : un clic dessus ne doit surtout pas ouvrir l'éditeur derrière la
 *  demande de confirmation. */
export function DishCard({ item, allergenTerms, detected, onEdit, onDelete }: {
    item: MenuItem;
    allergenTerms: any[];
    /** Allergènes déduits des ingrédients, calculés par la page (elle seule connaît le référentiel chargé). */
    detected: string[];
    onEdit: (item: MenuItem) => void;
    onDelete: (item: MenuItem) => void;
}) {
    return (
        <div role="button" tabIndex={0}
            onClick={() => onEdit(item)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(item); } }}
            title="Modifier ce plat"
            className="bg-surface-soft border border-border rounded-xl p-3.5 space-y-2 text-left cursor-pointer transition-colors hover:border-brand-500/40 hover:bg-surface focus:outline-none focus:border-brand-500">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <h4 className="text-sm font-bold text-content">{item.name}</h4>
                    {item.category && <p className="text-[10px] text-content-muted uppercase tracking-widest">{item.category}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={(e: any) => { e.stopPropagation(); onEdit(item); }} className="px-2!"><Pencil size={13} /></Button>
                    <Button variant="ghost" size="sm" onClick={(e: any) => { e.stopPropagation(); onDelete(item); }} className="px-2! text-red-400! hover:bg-red-500/10! hover:text-red-400!"><Trash2 size={13} /></Button>
                </div>
            </div>
            {item.description && <p className="text-xs text-content-muted">{item.description}</p>}
            {item.ingredients.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {item.ingredients.map((ing) => {
                        const comps = ing.components || [];
                        // Les composants apparaissent en info-bulle : les afficher tous alourdirait la carte,
                        // mais on doit pouvoir vérifier d'un survol ce que contient une préparation. Un
                        // composant lui-même retirable est signalé (✓) — il peut être écarté seul.
                        const title = [ing.removable ? 'Possible de retirer' : null, comps.length ? `Contient : ${comps.map((c) => c.removable ? `${c.name} ✓` : c.name).join(', ')}` : null]
                            .filter(Boolean).join(' — ') || undefined;
                        return (
                            <span key={ing.name} className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${ing.removable ? 'bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400' : 'bg-surface border-border text-content-muted'}`} title={title}>
                                {ing.name}{comps.length ? ` (${comps.length})` : ''}{ing.removable ? ' ✓' : ''}
                            </span>
                        );
                    })}
                </div>
            )}
            {(item.traces || []).length > 0 && (
                <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[10px] text-content-muted italic">Traces :</span>
                    {item.traces.map((t) => (
                        <span key={t} className="text-[10px] font-semibold bg-sky-500/10 border border-sky-500/20 rounded-full px-2 py-0.5 text-sky-700 dark:text-sky-300">{t}</span>
                    ))}
                </div>
            )}
            {/* Restrictions CALCULÉES à partir des ingrédients, pour tous les plats de la même façon. Les
                anciennes "restrictions incompatibles" saisies à la main n'étaient remplies que sur certains
                plats, d'où des croix rouges qui apparaissaient ici et pas là sans raison visible — question
                d'Emeni (2026-08-23). Elles ne sont plus affichées : le champ n'existe plus, et une donnée
                figée contredirait ce que le convive peut réellement faire retirer. */}
            {(() => {
                const blocked = RESTRICTION_GROUPS
                    .map((g: any) => {
                        const hits = ingredientsBlockingRestriction(g.name, item.ingredients, keywordsForRestriction(g.name, allergenTerms), allergenTerms);
                        if (!hits.length) return null;
                        // Le nom des ingrédients responsables est repris dans l'info-bulle : sans lui, une
                        // pastille « ✕ végétarien » sur un velouté de légumes est incompréhensible — ici
                        // c'est le bouillon de volaille du velouté qui la déclenche. Question d'Emeni
                        // (2026-08-23) : "d'où viennent ces restrictions impossibles ?".
                        return {
                            name: g.name,
                            adaptable: hits.every((h: any) => h.ingredient.removable || (h.via && h.via.removable)),
                            because: hits.map((h: any) => h.ingredient.name + (h.via ? ` (${h.via.name.toLowerCase()})` : '')),
                        };
                    })
                    .filter(Boolean) as { name: string; adaptable: boolean; because: string[] }[];
                if (!detected.length && !item.dietary_tags.length && !blocked.length) return null;
                return (
                    <div className="flex flex-wrap gap-1">
                        {detected.map((a) => <span key={a} className="text-[10px] font-semibold bg-warning-500/10 border border-warning-500/20 rounded-full px-2 py-0.5 text-warning-600 dark:text-warning-400">{a}</span>)}
                        {item.dietary_tags.map((d) => <span key={d} className="text-[10px] font-semibold bg-brand-500/10 border border-brand-500/20 rounded-full px-2 py-0.5 text-brand-600 dark:text-brand-400">{d}</span>)}
                        {blocked.map((b) => (
                            <span key={b.name} title={`${b.adaptable ? 'Adaptable en retirant' : 'Non adaptable — à cause de'} : ${b.because.join(', ')}`}
                                className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${b.adaptable ? 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
                                {b.adaptable ? '~' : '✕'} {b.name.toLowerCase()}
                            </span>
                        ))}
                    </div>
                );
            })()}
            {/* Variantes héritées de l'ancien modèle : plus éditables, seulement signalées tant qu'elles
                existent. Le bouton « Convertir les variantes » les remplace par des ingrédients retirables,
                après quoi ce bloc disparaît de lui-même. */}
            {(item.variants || []).length > 0 && (
                <div className="space-y-1 pt-1 border-t border-amber-500/30">
                    <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest">Anciennes variantes — à convertir</p>
                    {item.variants.map((v) => (
                        <p key={v.id} className="text-[11px] text-content-muted">
                            <span className="font-semibold text-content">{v.label}</span>
                            {v.resolves_restrictions.length > 0 && ` — résout : ${v.resolves_restrictions.join(', ')}`}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Carte d'une formule. Même parti pris que la carte plat : toute la carte ouvre l'éditeur. */
export function FormulaCard({ menu, diets, onEdit, onDelete }: {
    menu: Menu;
    /** Régimes servables de bout en bout, calculés par la page (formulaDietBadges). */
    diets: DietBadge[];
    onEdit: (menu: Menu) => void;
    onDelete: (menu: Menu) => void;
}) {
    return (
        <div role="button" tabIndex={0}
            onClick={() => onEdit(menu)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(menu); } }}
            title="Modifier cette formule"
            className="bg-surface-soft border border-border rounded-xl p-3.5 space-y-2 text-left cursor-pointer transition-colors hover:border-brand-500/40 hover:bg-surface focus:outline-none focus:border-brand-500">
            <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-bold text-content">{menu.name}</h4>
                <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={(e: any) => { e.stopPropagation(); onEdit(menu); }} className="px-2!"><Pencil size={13} /></Button>
                    <Button variant="ghost" size="sm" onClick={(e: any) => { e.stopPropagation(); onDelete(menu); }} className="px-2! text-red-400! hover:bg-red-500/10! hover:text-red-400!"><Trash2 size={13} /></Button>
                </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {menu.structure.map((s) => (
                    <span key={s.category} className="text-[11px] font-semibold bg-surface border border-border rounded-full px-2.5 py-1 text-content-muted">
                        {s.category} · {s.dish_ids.length}
                    </span>
                ))}
            </div>
            {/* Régimes réellement servables par cette formule — un régime n'est retenu que si CHAQUE
                catégorie lui laisse un plat : une entrée et un dessert végétariens sans plat ne
                servent à rien. Vert = tel quel, orange = en retirant un élément en cuisine.
                Demande d'Emeni (2026-08-25). */}
            {diets.length > 0 ? (
                <div className="flex flex-wrap gap-1 pt-0.5">
                    {diets.map((d) => (
                        <span key={d.name}
                            title={d.level === 'ok' ? 'Un plat convient tel quel dans chaque catégorie' : 'Servable dans chaque catégorie, en retirant un élément'}
                            className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${d.level === 'ok' ? 'bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400' : 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400'}`}>
                            {d.level === 'ok' ? '✓' : '~'} {d.name.toLowerCase()}
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-[10px] text-content-muted italic pt-0.5">Aucun régime particulier servable de bout en bout avec cette formule.</p>
            )}
        </div>
    );
}

/** Légende des pastilles d'une carte plat. Sans elle, rien n'indiquait ce que voulaient dire le gris, le
 *  vert ou le rouge — il fallait ouvrir la fiche pour deviner. Aucune donnée, aucune propriété : c'est
 *  purement explicatif, d'où sa place ici plutôt que dans la page. */
export function DishBadgeLegend() {
    return (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-content-muted">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-surface-soft border border-border" /> Ingrédient</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500/40 border border-green-500/40" /> Retirable</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500/40 border border-sky-500/40" /> Traces possibles</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning-500/40 border border-warning-500/40" /> Allergène</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-500/40 border border-brand-500/40" /> Exception déclarée</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500/40 border border-amber-500/40" /> ~ Restriction adaptable</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500/40 border border-red-500/40" /> ✕ Restriction impossible</span>
                    </div>
    );
}
